import { configureStore, lruMemoize, ReducersMapObject } from '@reduxjs/toolkit';
import localStorageModule from 'redux-persist/lib/storage';
import sessionStorageModule from 'redux-persist/lib/storage/session';
import {
    FLUSH,
    PAUSE,
    PERSIST,
    persistReducer,
    persistStore,
    PURGE,
    REGISTER,
    REHYDRATE,
} from 'redux-persist';
import { encryptTransform } from 'redux-persist-transform-encrypt';
import authReducer from '../state/auth/authSlice';
import memberReducer from '../state/member/memberSlice';
import generalReducer from '../state/general/generalSlice';
import exchangesReducer from '../state/exchanges/exchangesSlice';

import storageReducer from '../state/storage/storageSlice';
import legsReducer from '../state/legs/legsSlice';
import balancesReducer from '../state/balances/balancesSlice';
import journalsReducer from '../state/journals/journalsSlice';
import paymentsReducer from '../state/payments/paymentsSlice';
import verificationReducer from '../state/verification/verificationSlice';
import bannersReducer from '../state/banners/bannersSlice';
import shortStorageReducer from '../state/shortStorage/shortStorageSlice';
import orderReducer from '../state/order/orderSlice';
import notificationsReducer from '../state/notifications/notificationsSlice';
import leaderboardReducer from '../state/leaderboard/leaderboardSlice';
import intelligence2Reducer from '@/state/intelligence2/intelligence2Slice';
import hypertrackerReducer from '@/state/hypertracker/hypertackerSlice';
import calendearReducer from '../state/calendar/calendarSlice';
import clientStorageReducer from '@/state/clientStorage/clientStorageSlice';
import { createSelectorCreator } from 'reselect';
import isEqual from 'lodash/isEqual';

type PersistStorage = {
    getItem: (key: string) => Promise<string | null>;
    setItem: (key: string, item: string) => Promise<void>;
    removeItem: (key: string) => Promise<void>;
};

const noopStorage: PersistStorage = {
    getItem: () => Promise.resolve(null),
    setItem: () => Promise.resolve(),
    removeItem: () => Promise.resolve(),
};

const normalizePersistStorage = (storageModule: unknown): PersistStorage => {
    const storageCandidate = storageModule as
        | PersistStorage
        | {
              default?: PersistStorage;
          };
    const resolvedStorage =
        storageCandidate && 'getItem' in storageCandidate
            ? storageCandidate
            : storageCandidate?.default;

    if (
        resolvedStorage &&
        typeof resolvedStorage.getItem === 'function' &&
        typeof resolvedStorage.setItem === 'function' &&
        typeof resolvedStorage.removeItem === 'function'
    ) {
        return resolvedStorage;
    }

    return noopStorage;
};

const storage = normalizePersistStorage(localStorageModule);
const storageSession = normalizePersistStorage(sessionStorageModule);

export const createDeepEqualSelector = createSelectorCreator(lruMemoize, {
    equalityCheck: isEqual,
    maxSize: 20,
    resultEqualityCheck: isEqual,
});

const migrate = (state, version) => {
    if (state && (state.hasOwnProperty('tokens') || state.hasOwnProperty('isAuth'))) {
        return Promise.resolve(state);
    }
    if (state && state._persist && state._persist.version !== version) {
        return Promise.resolve({});
    }
    if (state && state?.exchangeAccounts && !Array.isArray(state?.exchangeAccounts)) {
        return Promise.resolve({});
    }

    if (state && state?.payments?.discountPercentage && state?.payments?.exchangeAccounts > 11) {
        return Promise.resolve({});
    }

    return Promise.resolve(state);
};

const version = __APP_VERSION__ ? +__APP_VERSION__ : -1;

const hash: string = import.meta.env.REACT_APP_HASH || '';
const env = import.meta.env.REACT_APP_ENVR || 'dev';

const authConfig = {
    key: `@coin_${env}_auth_config`,
    storage,
    migrate,
    version,
};
const memberConfig = {
    key: `@coin_${env}_member_config`,
    storage,
    migrate,
    version,
};

const generalConfig = {
    key: `@coin_${env}_general_config`,
    storage,
    whitelist: ['storedTheme', 'assets', 'countries'],
    migrate,
    version,
};

const exchangesConfig = {
    key: `@coin_${env}_exchanges_config`,
    storage,
    blacklist: [
        'isSecondDrawerVisible',
        'accountBalances',
        'derivativePositions',
        'selectedAccounts',
        'isRefreshError',
    ],
    migrate,
    version,
};

const journalConfig = {
    key: `@coin_${env}_journal_config`,
    storage,
    blacklist: ['entryDetails', 'journalEntries', 'threads', 'notes'],
    migrate,
    version,
};

const legsConfig = {
    key: `@coin_${env}_legs_recheck_config`,
    storage,
    blacklist: ['legsList'],
    migrate,
    version,
};

const storageConfig = {
    key: `@coin_${env}_storage_config`,
    storage,
    migrate,
    version,
};

const balancesConfig = {
    key: `@coin_${env}_balances_config`,
    storage,
    blacklist: ['depositWithdrawal'],
    migrate,
    version,
};

const paymentsConfig = {
    key: `@coin_${env}_payments_config`,
    storage,
    migrate,
    version,
    blacklist: [
        'userOrders',
        'subscriptions',
        'prices',
        'nonRedeemedCoupons',
        'discountPercentage',
        'discountCoupons',
    ],
};

const shortStorageConfig = {
    key: `@coin_${env}_short_storage_config`,
    storage: storageSession,
    migrate,
    version,
    blacklist: ['legsSorting'],
};

const verificationConfig = {
    key: `@coin_${env}_verification_config`,
    storage,
    migrate,
    version,
};

const bannersConfig = {
    key: `@coin_${env}_banners_config`,
    storage,
    migrate,
    version,
};

const orderConfig = {
    key: `@coin_${env}_order_config`,
    storage,
    migrate,
    version,
};

const notificationsConfig = {
    key: `@coin_${env}_notifications_config`,
    storage: storageSession,
    migrate,
    version,
};

const leaderBoardConfig = {
    key: `@coin_${env}_leaderboard_config`,
    storage: storageSession,
    migrate,
    version,
};
const intelligence2Config = {
    key: `@coin_${env}_intelligence2_config`,
    storage: storageSession,
    migrate,
    version,
};
const hypertrackerConfig = {
    key: `@coin_${env}_hypertracker_config`,
    storage: storageSession,
    migrate,
    version,
    blacklist: ['filters'],
};
const calendarConfig = {
    key: `@coin_${env}_calendar_config`,
    storage: storageSession,
    migrate,
    version,
};
const clientStorageConfig = {
    key: `@coin_${env}_client_storage_config`,
    storage: storageSession,
    migrate,
    version,
};

const reducer: ReducersMapObject = {
    auth: persistReducer(
        {
            ...authConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        authReducer,
    ),
    member: persistReducer(
        {
            ...memberConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        memberReducer,
    ),
    exchanges: persistReducer(
        {
            ...exchangesConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        exchangesReducer,
    ),
    general: persistReducer(generalConfig, generalReducer),
    storage: persistReducer(
        {
            ...storageConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        storageReducer,
    ),
    legs: persistReducer(
        {
            ...legsConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        legsReducer,
    ),
    balances: persistReducer(balancesConfig, balancesReducer),
    journals: persistReducer(
        {
            ...journalConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        journalsReducer,
    ),
    payments: persistReducer(
        {
            ...paymentsConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        paymentsReducer,
    ),
    verification: persistReducer(
        {
            ...verificationConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        verificationReducer,
    ),
    banners: persistReducer(bannersConfig, bannersReducer),
    shortStorage: persistReducer(
        {
            ...shortStorageConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        shortStorageReducer,
    ),
    order: persistReducer(orderConfig, orderReducer),
    notifications: persistReducer(notificationsConfig, notificationsReducer),
    leaderboard: persistReducer(leaderBoardConfig, leaderboardReducer),
    intelligence2: persistReducer(intelligence2Config, intelligence2Reducer),
    hypertracker: persistReducer(
        {
            ...hypertrackerConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        hypertrackerReducer,
    ),
    calendar: persistReducer(calendarConfig, calendearReducer),
    clientStorage: persistReducer(clientStorageConfig, clientStorageReducer),
};

const ignoredActions = [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER];

const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions,
                warnAfter: 128,
            },
            immutableCheck: { warnAfter: 128 },
        }),

    devTools: import.meta.env.MODE !== 'production',
});

/* eslint-disable */
export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export default store;
